import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Alert, CircularProgress } from '@mui/material';
import { useAuth } from './AuthContext';

export default function LoginPage() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const { error } = await signIn(email, password);
    if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#0a0a0a',
        backgroundImage: 'radial-gradient(ellipse at 50% 0%, rgba(37, 99, 235, 0.08) 0%, transparent 60%)',
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          width: 400,
          p: 5,
          backgroundColor: '#1a1a1a',
          border: '1px solid #374151',
          borderRadius: '8px',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
        }}
      >
        {/* Logo / Brand */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
              fontWeight: 700,
              color: '#ffffff',
              letterSpacing: '-0.02em',
            }}
          >
            GEOMORPHIC
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "'JetBrains Mono', monospace",
              color: '#94a3b8',
              mt: 0.5,
              fontSize: '0.75rem',
            }}
          >
            Prospector Engine
          </Typography>
        </Box>

        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              backgroundColor: 'rgba(220, 38, 38, 0.1)',
              color: '#fca5a5',
              border: '1px solid rgba(220, 38, 38, 0.3)',
              '& .MuiAlert-icon': { color: '#dc2626' },
            }}
          >
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoFocus
          sx={{
            mb: 2.5,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#161b22',
              color: '#ffffff',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              '& fieldset': { borderColor: '#374151' },
              '&:hover fieldset': { borderColor: '#4b5563' },
              '&.Mui-focused fieldset': { borderColor: '#2563eb' },
            },
            '& .MuiInputLabel-root': {
              color: '#94a3b8',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              '&.Mui-focused': { color: '#60a5fa' },
            },
          }}
        />

        <TextField
          fullWidth
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#161b22',
              color: '#ffffff',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              '& fieldset': { borderColor: '#374151' },
              '&:hover fieldset': { borderColor: '#4b5563' },
              '&.Mui-focused fieldset': { borderColor: '#2563eb' },
            },
            '& .MuiInputLabel-root': {
              color: '#94a3b8',
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: '0.875rem',
              '&.Mui-focused': { color: '#60a5fa' },
            },
          }}
        />

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={loading}
          sx={{
            py: 1.5,
            backgroundColor: '#2563eb',
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
            '&:hover': { backgroundColor: '#1d4ed8' },
            '&.Mui-disabled': { backgroundColor: '#1e3a8a', color: '#60a5fa' },
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#60a5fa' }} /> : 'Sign In'}
        </Button>

        <Typography
          sx={{
            mt: 3,
            textAlign: 'center',
            color: '#4b5563',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.7rem',
          }}
        >
          Access restricted to authorized personnel
        </Typography>
      </Box>
    </Box>
  );
}
